import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { avatarColor, initials } from "@/lib/utils2";

const ROLE_COLORS = {
  president: "#0A4D3C", tresorier: "#3254C6", conseil: "#8B5CF6",
  coproprietaire: "#64748B", gardien: "#B45309",
};

export default function Residents() {
  const { t } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [residents, setResidents] = useState([]);
  const [incoming, setIncoming] = useState([]);

  const load = useCallback(() => {
    api.get("/residents").then((r) => setResidents(r.data)).catch(() => toast.error(t("load_failed")));
    api.get("/contact-requests").then((r) => setIncoming(r.data)).catch(() => {});
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const requestContact = async (id) => {
    await api.post(`/residents/${id}/request-contact`);
    toast.success(t("request_sent"));
    load();
  };

  const respond = async (reqId, approve) => {
    await api.post(`/contact-requests/${reqId}/respond`, { approve });
    toast.success(approve ? t("authorize") : t("refuse"));
    load();
  };

  const waNumber = (phone) => (phone || "").replace(/[^0-9]/g, "");

  return (
    <div className="space-y-4" data-testid="residents-page">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate("/more")} className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center shrink-0" data-testid="residents-back">
          <Icons.ArrowLeft className="h-5 w-5 rtl:rotate-180" />
        </button>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold leading-tight">{t("residents")}</h1>
          <p className="text-sm text-muted-foreground">{t("residents_sub")}</p>
        </div>
      </div>

      {/* Bandeau vie privée */}
      <div className="rounded-2xl bg-accent p-3 flex items-start gap-2" data-testid="privacy-note">
        <Icons.ShieldCheck className="h-5 w-5 text-brand dark:text-primary shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground">{t("privacy_note")}</p>
      </div>

      {/* Demandes reçues (consentement) */}
      {incoming.length > 0 && (
        <div className="space-y-2" data-testid="incoming-requests">
          <h2 className="font-bold text-lg flex items-center gap-2">
            <Icons.BellRing className="h-5 w-5 text-terracotta" /> {t("incoming_requests")}
            <span className="bg-terracotta text-white text-xs rounded-full px-2 py-0.5">{incoming.length}</span>
          </h2>
          {incoming.map((r) => (
            <div key={r.id} className="rounded-2xl bg-card border border-terracotta/40 p-4" data-testid={`request-${r.id}`}>
              <p className="text-sm"><b>{r.from_display}</b>{r.from_apartment ? ` (${r.from_apartment})` : ""} {t("wants_contact")}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => respond(r.id, true)} data-testid={`approve-${r.id}`}
                  className="flex-1 h-10 rounded-xl bg-brand text-white font-semibold text-sm flex items-center justify-center gap-1.5">
                  <Icons.Check className="h-4 w-4" /> {t("authorize")}
                </button>
                <button onClick={() => respond(r.id, false)} data-testid={`decline-${r.id}`}
                  className="flex-1 h-10 rounded-xl bg-secondary text-secondary-foreground font-semibold text-sm flex items-center justify-center gap-1.5">
                  <Icons.X className="h-4 w-4" /> {t("refuse")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Annuaire */}
      <div className="space-y-2">
        {residents.map((r) => {
          const name = r.status === "self" || r.status === "visible" ? r.full_name : r.display_name;
          const color = ROLE_COLORS[r.role] || "#64748B";
          return (
            <div key={r.id} className="rounded-2xl bg-card border border-border p-4" data-testid={`resident-${r.id}`}>
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full flex items-center justify-center text-white font-bold shrink-0" style={{ backgroundColor: avatarColor(name) }}>
                  {initials(name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-bold truncate">{name}</p>
                    {r.status === "self" && <span className="text-[10px] font-bold bg-brand/10 text-brand dark:text-primary px-1.5 py-0.5 rounded-full shrink-0">{t("you_tag")}</span>}
                  </div>
                  <p className="text-xs truncate" style={{ color }}>{t("role_" + r.role)}{r.apartment ? ` · ${t("apartment")} ${r.apartment}` : ""}</p>
                </div>
              </div>

              {r.status === "hidden" && (
                <button onClick={() => requestContact(r.id)} data-testid={`request-btn-${r.id}`}
                  className="mt-3 w-full h-10 rounded-xl bg-brand text-white text-sm font-semibold flex items-center justify-center gap-1.5">
                  <Icons.UserPlus className="h-4 w-4" /> {t("request_contact")}
                </button>
              )}
              {r.status === "pending" && (
                <span className="mt-3 w-full h-10 rounded-xl bg-secondary text-secondary-foreground text-sm font-semibold flex items-center justify-center gap-1.5" data-testid={`pending-${r.id}`}>
                  <Icons.Clock className="h-4 w-4" /> {t("request_sent")}
                </span>
              )}

              {/* Coordonnées si autorisé ou soi-même */}
              {(r.status === "visible" || r.status === "self") && (r.phone || r.email) && (
                <div className="mt-3 pt-3 border-t border-border space-y-2" data-testid={`contact-${r.id}`}>
                  {r.email && <p className="text-sm flex items-center gap-2 text-muted-foreground"><Icons.Mail className="h-4 w-4" /> {r.email}</p>}
                  {r.phone && r.status !== "self" && (
                    <div className="flex gap-2">
                      <a href={`tel:${r.phone}`} data-testid={`res-call-${r.id}`}
                        className="flex-1 h-10 rounded-xl bg-brand text-white text-sm font-semibold flex items-center justify-center gap-1.5">
                        <Icons.Phone className="h-4 w-4" /> {r.phone}
                      </a>
                      <a href={`https://wa.me/${waNumber(r.phone)}`} target="_blank" rel="noreferrer" data-testid={`res-wa-${r.id}`}
                        className="h-10 w-12 rounded-xl bg-[#25D366] text-white flex items-center justify-center">
                        <Icons.MessageCircle className="h-4 w-4" />
                      </a>
                    </div>
                  )}
                  {r.phone && r.status === "self" && (
                    <p className="text-sm flex items-center gap-2 text-muted-foreground"><Icons.Phone className="h-4 w-4" /> {r.phone}</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
